import { el } from '../../core/dom.js';
import { formatRelative } from '../../core/format.js';
import { withProduct } from '../../core/route-map.js';
import { skeleton } from '../../components/skeleton.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { syncNowButton } from '../../components/sync-now.js';
import { firstReadLead, openInsightDetail } from './insight-tools.js';
import {
  insightCardPreview,
  scheduleDisplayTitle,
  scheduleCardMeta,
  kpiConnectHint,
  sanitizeKpiTrend,
  kpiHasReading,
} from './copy.js';

const KPI_BY_ID = {
  mau: 'monthly actives',
  act: 'activation',
  d7: 'd7',
  paid: 'trial',
};

const DEPTH_LABELS = [
  ['analytics', 'Analytics'],
  ['code', 'Code'],
  ['revenue', 'Revenue'],
  ['ads', 'Ads'],
];

const LIVE_HEALTH = new Set(['live', 'partial', 'expiring', 'healthy', 'unverified', 'quota_limited', 'ok']);

function failed(payload) {
  return !!(payload && payload.error);
}

function cardShell(title, body) {
  return el('article', { class: 'dash-w', 'aria-label': title },
    el('div', { class: 'dash-wbody' }, body));
}

function stateBody(payload, emptyNode, ready) {
  if (failed(payload)) {
    return errorPanel({ title: 'Could not load this card', message: payload.error });
  }
  return ready(payload, emptyNode);
}

function findKpi(list, id) {
  const needle = KPI_BY_ID[id];
  return (list || []).find((item) => String(item.label || '').toLowerCase().includes(needle)) || null;
}

const KPI_CODE = { mau: 'KPI-01', act: 'KPI-02', d7: 'KPI-03', paid: 'KPI-04' };

function parseNum(value) {
  const match = String(value || '').replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

function sparkPath(values) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = values.length === 1 ? 0 : 200 / (values.length - 1);
  return values.map((value, index) => {
    const x = (index * step).toFixed(1);
    const y = (34 - ((value - min) / span) * 28).toFixed(1);
    return `${x} ${y}`;
  }).join(' L');
}

function sparkline(kpi) {
  const series = Array.isArray(kpi.series) ? kpi.series.map(Number).filter((n) => Number.isFinite(n)) : [];
  const end = parseNum(kpi.value);
  const start = parseNum(kpi.prior);
  const values = series.length >= 2 ? series : (end != null && start != null ? [start, end] : null);
  if (!values) return null;
  const line = `M${sparkPath(values)}`;
  return el('svg', {
    class: 'dash-spark',
    viewBox: '0 0 200 40',
    width: '100%',
    height: '40',
    preserveAspectRatio: 'none',
    'aria-hidden': 'true',
  },
  el('path', { d: `${line} L200 40 L0 40 Z`, fill: 'var(--signal-tint)' }),
  el('path', { class: 'dash-spark-line', d: line, fill: 'none', stroke: 'var(--signal)', 'stroke-width': '1.75' }));
}

export function kpiCard(id, insights, productId) {
  const title = id === 'mau' ? 'Monthly actives'
    : id === 'act' ? 'Activation rate'
      : id === 'd7' ? 'D7 retention'
        : 'Trial → paid';
  if (failed(insights)) return cardShell(title, errorPanel({ title, message: insights.error }));
  const kpi = findKpi(insights.kpis, id);
  if (!kpiHasReading(kpi)) {
    const hint = kpiConnectHint(id);
    return cardShell(title, empty({
      title: hint.message,
      body: 'Add the connector for this product and the reading shows up here.',
      action: button('Open connectors', {
        href: withProduct('/app/connectors', productId),
      }),
    }));
  }
  const trend = sanitizeKpiTrend(kpi.trend);
  const down = /↓|down|-\d/.test(trend);
  return cardShell(title, el('div', { class: 'dash-kpi' },
    el('div', { class: 'dash-kicker dash-rule' },
      el('span', {}, KPI_CODE[id] || 'KPI'),
      el('span', {}, 'Last 30 days')),
    el('div', { class: 'dash-kicker is-name' }, title),
    el('div', { class: 'dash-kpi-value' },
      el('span', { class: 'dash-figure' }, kpi.value),
      trend ? el('span', { class: down ? 'dash-delta is-down' : 'dash-delta' }, trend) : null),
    sparkline(kpi),
    el('div', { class: 'dash-kicker' },
      el('span', {}, kpi.prior ? `vs prior ${kpi.prior}` : 'Last 30 days'))));
}

export function insightsCard(insights, onRefresh, productId) {
  if (failed(insights)) {
    return cardShell('Ranked insights', errorPanel({
      title: 'Ranked insights',
      message: insights.error,
      action: button('Refresh now', { onClick: onRefresh }),
    }));
  }
  const list = insights.insights || [];
  if (!list.length) {
    return cardShell('Ranked insights', el('div', { class: 'dash-stack' },
      firstReadLead(insights, productId),
      el('div', { class: 'dash-kicker dash-rule-solid' },
        el('span', {}, 'This week, ranked by what matters'),
        el('span', {}, '0 insights')),
      el('div', { class: 'dash-insight-lead' },
        el('div', {},
          el('h2', { class: 'dash-serif' }, 'No ranked insights ', el('em', {}, 'yet.')),
          el('p', { class: 'dash-copy' }, 'The feed fills from connectors and product memory on the next refresh.')),
        button('Refresh now', { variant: 'primary', onClick: onRefresh })),
      el('div', { class: 'dash-wait' }, [42, 30, 22].map((width, index) => el('div', { class: 'dash-wait-row' },
        el('span', { class: 'dash-index' }, String(index + 1).padStart(2, '0')),
        el('span', { class: 'skel', style: `width:${width}%` }),
        el('span', { class: 'dash-note' }, 'Waiting'))))));
  }
  return cardShell('Ranked insights', el('div', { class: 'dash-stack' },
    firstReadLead(insights, productId),
    el('div', { class: 'dash-kicker' },
      el('span', {}, 'This week, ranked by what matters'),
      el('span', {}, `${list.length} insights`)),
    el('ol', { class: 'dash-list' }, list.slice(0, 5).map((item, index) => {
      const preview = insightCardPreview(item);
      return el('li', {},
        el('span', { class: 'dash-index' }, String(index + 1).padStart(2, '0')),
        el('button', {
          type: 'button',
          class: 'dash-list-main dash-insight-open',
          onClick: () => openInsightDetail(item, productId),
        },
        el('span', { class: 'dash-list-title' }, preview.title),
        preview.summary
          ? el('span', { class: 'dash-note dash-insight-summary' }, preview.summary)
          : null));
    }))));
}

export function depthCard(depth, productId) {
  const payload = depth || {};
  const pct = Number(payload.analysis_depth) || 0;
  const breakdown = payload.depth_breakdown || {};
  const parts = DEPTH_LABELS.map(([key, label]) => ({
    key, label, value: Number(breakdown[key]) || 0,
  }));
  return cardShell('Analysis depth', el('div', { class: 'dash-stack' },
    el('div', { class: 'dash-kicker' },
      el('span', {}, 'Analysis depth'),
      el('a', { href: withProduct('/app/connectors', productId) }, 'Details →')),
    el('p', { class: 'dash-depth' }, String(pct), el('em', {}, '%')),
    el('div', { class: 'dash-bar', 'aria-hidden': 'true' }, parts.map((part) => el('span', {
      style: `flex:${part.value || 0.4};${part.value ? '' : 'border:1px dashed var(--accent)'}`,
    }))),
    el('ul', { class: 'dash-depth-legend' }, parts.map((part) => el('li', {
      class: part.value ? '' : 'is-blind',
    }, `${part.value ? '■' : '□'} ${part.label} ${part.value || '— blind'}`)))));
}

export function setupCard(insights, productId) {
  if (failed(insights)) return cardShell('Setup progress', errorPanel({ title: 'Setup', message: insights.error }));
  const setup = insights.setup;
  if (!setup || !Array.isArray(setup.steps) || !setup.steps.length) {
    return cardShell('Setup progress', empty({
      title: 'Setup has not started',
      body: 'Add a product to see the remaining steps.',
      action: button('Add a product', { href: '/app/products/new', variant: 'primary' }),
    }));
  }
  const done = setup.done != null ? setup.done : setup.steps.filter((step) => step.done).length;
  const total = setup.total || setup.steps.length;
  const next = setup.steps.find((step) => !step.done);
  return cardShell('Setup progress', el('div', { class: 'dash-stack' },
    el('div', { class: 'dash-kicker' }, el('span', {}, 'Setup'), el('span', {}, `${done} / ${total}`)),
    el('div', { class: 'dash-steps', 'aria-hidden': 'true' }, setup.steps.map((step) => el('span', {
      class: step.done ? 'is-done' : '',
    }))),
    next
      ? el('a', { href: withProduct('/app/products/new', productId) }, `Finish product setup →`)
      : el('p', { class: 'dash-copy' }, 'Setup is complete.')));
}

export function reportsCard(schedules, productId) {
  if (failed(schedules)) return cardShell('Scheduled reports', errorPanel({ title: 'Scheduled reports', message: schedules.error }));
  const list = schedules.schedules || [];
  const href = withProduct('/app/schedules', productId);
  if (!list.length) {
    return cardShell('Scheduled reports', empty({
      title: 'No scheduled reports',
      body: 'A recurring question lands here after you save one.',
      action: button('Open schedules', { href }),
    }));
  }
  const active = list.filter((row) => row.enabled !== false).length;
  let highlighted = false;
  return cardShell('Scheduled reports', el('div', { class: 'dash-stack' },
    el('div', { class: 'dash-kicker' }, el('span', {}, 'Scheduled reports'), el('span', {}, `${active} active`)),
    list.slice(0, 3).map((row) => {
      const on = row.enabled !== false && !highlighted;
      if (on) highlighted = true;
      return el('a', {
        class: on ? 'dash-report is-on' : 'dash-report',
        href,
        title: String(row.prompt || '').trim() || undefined,
      },
      el('span', {}, scheduleDisplayTitle(row, 60)),
      el('span', { class: 'dash-note' }, scheduleCardMeta(row, formatRelative)));
    }),
    el('a', { href }, 'All schedules →')));
}

function connectorName(row) {
  return row.label || row.provider_display || row.provider_name || 'Connector';
}

function connectorLive(row) {
  const health = String(row.health_status || 'live').toLowerCase();
  return LIVE_HEALTH.has(health);
}

export function connectorsCard(connectors, productId, admin) {
  if (failed(connectors)) return cardShell('Connector health', errorPanel({ title: 'Connector health', message: connectors.error }));
  const list = connectors.integrations || [];
  const href = withProduct('/app/connectors', productId);
  if (!list.length) {
    return cardShell('Connector health', empty({
      title: 'No connectors yet',
      body: 'Connect a source to give Bonnie something to read.',
      action: button('Add a connector', { href, variant: 'primary' }),
    }));
  }
  const live = list.filter(connectorLive).length;
  return cardShell('Connector health', el('div', { class: 'dash-stack' },
    el('div', { class: 'dash-kicker' }, el('span', {}, 'Connector health'), el('span', {}, `${live} live`)),
    el('ul', { class: 'dash-health' }, list.slice(0, 6).map((row) => {
      const on = connectorLive(row);
      return el('li', {},
        el('span', { class: on ? 'dash-dot is-on' : 'dash-dot', 'aria-hidden': 'true' }),
        el('span', { class: 'dash-health-name' }, connectorName(row)),
        el('span', { class: 'dash-note' }, on ? 'Live' : (row.health_status || 'Not connected')),
        admin && row.mode === 'synced' && row.source_id
          ? syncNowButton(row.source_id, 'btn btn-ghost')
          : null);
    })),
    el('a', { href }, 'Manage connectors →')));
}

export function chatsCard(chats, productId, productName) {
  if (failed(chats)) return cardShell('Recent chats', errorPanel({ title: 'Recent chats', message: chats.error }));
  const list = chats.groups || [];
  if (!list.length) {
    return cardShell('Recent chats', empty({
      title: 'No chats yet',
      body: 'Ask Bonnie a question to start one.',
      action: button('Ask Bonnie', { href: withProduct('/app/chat/new', productId), variant: 'primary' }),
    }));
  }
  return cardShell('Recent chats', el('div', { class: 'dash-stack' },
    el('div', { class: 'dash-kicker' },
      el('span', {}, 'Recent chats'),
      el('span', {}, productName || 'This product')),
    el('ul', { class: 'dash-chats' }, list.slice(0, 5).map((group) => el('li', {},
      el('a', { href: withProduct(`/app/chat/${group.id}`, productId) },
        el('span', {}, group.name || 'Chat'),
        el('span', { class: 'dash-note' }, formatRelative(group.last_message_at || group.updated_at)))))),
    el('a', { href: withProduct('/app/chat/new', productId) }, 'New chat →')));
}

export function loadingCard(title) {
  return cardShell(title, skeleton(3));
}

export { failed };
